import { CIMSPage, FieldDesignChangeStatus } from 'shared'
import { HelpMessage } from 'utils/constants'
import { ExpandOptions } from 'components/CIMSTable/CIMSTableContext/types/expand'
import { useFDCTrackerContext } from '../../../FDCTrackerContext'
import { FieldDesignChange } from '../../../FDCTrackerContext/types'
import { SingleUser } from 'components/CIMSTable/common/hooks/useSingleUserColumn/components/SingleUser'

export function useExpandOptions(): ExpandOptions<FieldDesignChange> {
  const help = HelpMessage.FieldDesignChanges.FDCTracker
  const {
    fieldDesignChanges,
    mutations: { comment, edit },
  } = useFDCTrackerContext()
  return {
    render: ({
      id,
      location,
      specsAffected,
      drawingsAffected,
      requestedApprovalDate,
      requestedBy,
      fdcToPayItemNoGeometries,
      designSubmittals,
      submittedDate,
      comments,
      customFields,
      deleted,
      statusId,
      issue,
    }) => ({
      additionalInfo: [
        {
          label: 'Location',
          children: location,
          help: help.Location,
        },
        {
          label: 'Specs Affected',
          children: specsAffected,
          help: help.SpecsAffected,
        },
        {
          label: 'Drawings Affected',
          children: drawingsAffected,
          help: help.DrawingsAffected,
        },
        {
          label: 'Requested Approval Date',
          type: 'date',
          date: requestedApprovalDate,
          help: help.RequestedApprovalDate,
        },
        {
          label: 'Requested By',
          children: requestedBy && <SingleUser {...requestedBy} />,
          help: help.RequestedBy,
        },
        {
          label: 'Submitted Date',
          type: 'date',
          date: submittedDate,
          help: help.SubmittedDate,
        },
        {
          type: 'payItems',
          payItems: fdcToPayItemNoGeometries.map(({ payItem }) => payItem),
          object: 'Field Design Change',
        },
        {
          label: 'Design Submittals',
          help: help.DesignSubmittals,
          type: 'deepLinks',
          showMore: { key: id },
          links: designSubmittals.map(
            ({ packageName, deleted, id, packageNumber }) => ({
              label: packageNumber,
              tooltip: packageName,
              page: CIMSPage.DSLog,
              deep: {
                recordId: id,
                deleted,
              },
            })
          ),
        },
      ],
      comments: {
        comments,
        permission: comment.can,
        onSave: async (text) => {
          await comment.fn({
            variables: { input: { fieldDesignChangeId: id, text } },
          })
        },
      },
      customFields: {
        customFields,
        alreadyDisabled:
          statusId === FieldDesignChangeStatus.Closed.id
            ? {
                why: 'invalid status',
                statusName: FieldDesignChangeStatus.Closed.name,
              }
            : false,
        recordInactive: deleted,
        booleanValueOptions: {
          onCheck: async (input) => {
            await edit.fn({
              variables: {
                input: { id, issue, customFields: { booleanInputs: [input] } },
              },
            })
            fieldDesignChanges?.refetch()
          },
        },
        listValueOptions: {
          onCheck: async (input) => {
            await edit.fn({
              variables: {
                input: {
                  id,
                  issue,
                  customFields: { listInputs: { checkboxes: [input] } },
                },
              },
            })
            fieldDesignChanges?.refetch()
          },
        },
      },
    }),
  }
}
